import { useCallback, useEffect, useRef, useState } from 'react';
import { createChatIdempotencyKey, isChatConnectionError } from './chatConnectionError';
import {
  assistantPatchFromSendPayload,
  deferredSourceNotice,
  followUpContextNotice,
  latestArtifactContext,
  latestCompanyCount,
  newMsg,
} from './chatMessageModel';
import { reconcileChatTurn, submitChatTurn } from './chatSendTurn';
import { STALLED_TURN_NOTICE } from './useStalledChatTurns';

export const THINKING_PLACEHOLDER = 'Thinking…';

/**
 * One user send = one idempotency key (FE #746). A send whose connection drops
 * keeps its attempt so Retry re-posts the same key to the same assistant turn.
 */
export default function useChatSend({
  sessionId,
  messages,
  setMessages,
  processing,
  setProcessing,
  setProcessingId,
  clearRestoreError,
  ensureSession,
  applySessionData,
  finishJobForMessage,
  isAbandoned,
  isMounted,
  refreshSessions,
  updateAssistantMessage,
}) {
  const [sendRetry, setSendRetry] = useState(null);
  const activeSend = useRef(null);
  const recovery = useRef(null);

  useEffect(() => () => recovery.current?.stop(), []);
  useEffect(() => {
    const pending = recovery.current;
    if (pending && (pending.sessionId !== sessionId || !messages.some((message) =>
      message.id === pending.messageId && !message.stalled))) pending.stop();
  }, [messages, sessionId]);

  const runSendAttempt = useCallback(
    async (attempt) => {
      const { text, idempotencyKey, assistantMsgId, previousCount, artifactContext, priorMessages } = attempt;
      const token = {};
      activeSend.current = token;
      let trackedId = assistantMsgId;
      const startedAt = Date.now();
      const isCancelled = () => !isMounted() || activeSend.current !== token || isAbandoned(trackedId);
      setSendRetry(null);
      setProcessing(true);
      setProcessingId(assistantMsgId);

      try {
        const activeSessionId = await ensureSession();
        if (isCancelled()) return;
        const { data: messageData, landedSession, pendingMessage } = await submitChatTurn({
          sessionId: activeSessionId, text, idempotencyKey, priorMessages, isCancelled,
        });
        if (isCancelled()) return;
        refreshSessions();
        if (landedSession) {
          if (pendingMessage) {
            trackedId = pendingMessage.message_id;
            // Follow the server's identity but retain the original send's clock.
            await applySessionData({ ...landedSession, messages: landedSession.messages.map((message) =>
              message === pendingMessage ? { ...message, created_at: new Date(startedAt).toISOString() } : message) }, isCancelled);
            if (isCancelled()) return;
            setProcessingId(trackedId);
            const task = reconcileChatTurn({
              sessionId: activeSessionId, pendingMessage, startedAt, isCancelled,
              onResolved: async (data, cancelled) => {
                recovery.current = null;
                await applySessionData(data, cancelled);
              },
              onStalled: () => {
                updateAssistantMessage(trackedId, { content: STALLED_TURN_NOTICE, stalled: true });
                setProcessing(false);
                setProcessingId(null);
              },
            });
            recovery.current = { ...task, sessionId: activeSessionId, messageId: trackedId };
            await task.done;
            if (recovery.current?.done === task.done) recovery.current = null;
            return;
          }
          // The server took the turn before the browser lost its reply: show it as stored.
          await applySessionData(landedSession, isCancelled);
          return;
        }

        updateAssistantMessage(assistantMsgId, {
          ...assistantPatchFromSendPayload(messageData),
          notice: deferredSourceNotice(messageData) || followUpContextNotice(messageData, artifactContext),
        });

        if (messageData.status !== 'accepted' || !messageData.job_id) {
          return;
        }

        updateAssistantMessage(assistantMsgId, {
          jobId: messageData.job_id,
          jobStatus: messageData.status,
        });
        await finishJobForMessage(assistantMsgId, messageData.job_id, {
          initialJob: { job_id: messageData.job_id, status: messageData.status },
          refinement: Boolean(messageData.refinement),
          previousCount,
        });
        refreshSessions();
      } catch (error) {
        if (isCancelled()) return;
        if (isChatConnectionError(error)) setSendRetry(attempt);
        updateAssistantMessage(assistantMsgId, {
          content: error.message || 'Unable to complete this chat request.',
        });
      } finally {
        if (!isCancelled()) {
          setProcessing(false);
          setProcessingId(null);
        }
      }
    },
    [
      applySessionData,
      ensureSession,
      finishJobForMessage,
      isAbandoned,
      isMounted,
      refreshSessions,
      setProcessing,
      setProcessingId,
      updateAssistantMessage,
    ]
  );

  const handleSend = useCallback(
    async (text) => {
      if (processing) return;
      clearRestoreError();
      const userMsg = newMsg('user', text);
      const assistantMsg = newMsg('assistant', THINKING_PLACEHOLDER);
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      await runSendAttempt({
        text,
        idempotencyKey: createChatIdempotencyKey(),
        assistantMsgId: assistantMsg.id,
        previousCount: latestCompanyCount(messages),
        artifactContext: latestArtifactContext(messages),
        priorMessages: messages,
      });
    },
    [clearRestoreError, messages, processing, runSendAttempt, setMessages]
  );

  // A retry is only live while its assistant turn is still on screen: a new chat or
  // a restored session replaces the thread and so retires it.
  const activeRetry = sendRetry && messages.some((message) => message.id === sendRetry.assistantMsgId)
    ? sendRetry
    : null;

  const handleRetrySend = useCallback(() => {
    if (!activeRetry || processing) return undefined;
    updateAssistantMessage(activeRetry.assistantMsgId, {
      content: THINKING_PLACEHOLDER,
      // Restart the stalled-turn clock for the retried turn.
      createdAt: new Date().toISOString(),
    });
    return runSendAttempt(activeRetry);
  }, [activeRetry, processing, runSendAttempt, updateAssistantMessage]);

  return { handleSend, handleRetrySend: activeRetry ? handleRetrySend : null };
}
